import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import { loadSession } from "../auth";
import {
  getTrainingById,
  submitQuiz,
  getProgress,
  saveProgress,
  isCertificateAllowed,
  signCertificate,
  getUserCompletionStatus,
} from "../services/api";
import { shouldBlockFeatureLoads } from "../utils/onboardingGate";
import { useTranslation, Trans } from "react-i18next";

export default function TrainingPlayer() {
  const { id } = useParams(); // training slug
  const nav = useNavigate();
  const user = loadSession()?.user;
  const { t } = useTranslation();

  const [training, setTraining] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // quiz state
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // dynamic quiz
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizStep, setQuizStep] = useState(null);

  // per-kind cert state
  const [certAllowed, setCertAllowed] = useState(null); // {allowed,status,requiredKinds,have:{SSM:{},SU:{}}}
  const [workingKind, setWorkingKind] = useState(null); // "SSM"/"SU" when generating or uploading
  const [signFlows, setSignFlows] = useState(null); // [{kind, id, flowId, lastStep, status}]
  // single-button flow handled by `signCertificate`

  // Load training + resume progress
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setTraining(null);
    setResult(null);
    setAnswers([]);
    setCertAllowed(null);
    // If onboarding/tenant gate is active, don't mount normal training loaders
    if (shouldBlockFeatureLoads()) {
      setLoading(false);
      nav("/onboarding", { replace: true });
      return () => { alive = false; };
    }
    (async () => {
      try {
        const tRes = await getTrainingById(id);
        if (!alive) return;
        setTraining(tRes);

        try {
          const p = await getProgress(id); // { lastStepIndex }
          if (!alive) return;
          const maxIdx = Math.max(0, (tRes?.steps?.length || 1) - 1);
          const idx = Math.min(Math.max(0, (p?.lastStepIndex ?? 0)), maxIdx);
          setStepIndex(idx);
        } catch {
          setStepIndex(0);
        }
      } catch (e) {
        if (alive) setError(e?.message || t("training_player.error_generic"));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Persist progress whenever stepIndex changes (and training loaded)
  useEffect(() => {
    if (!training) return;
    saveProgress(id, stepIndex).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, training, stepIndex]);

  // Refresh allowed state (overall + per-kind have map)
  // Refresh allowed state (overall + per-kind have map) — MERGING, not overwriting
  async function refreshAllowed() {
    try {
      const a = await isCertificateAllowed(id); // may not include "have" or "requiredKinds"
      setCertAllowed((prev) => {
        const next = { ...(a || {}) };

        // Preserve/merge HAVE map if backend didn't provide one
        if (!a?.have) {
          next.have = prev?.have || {};
        } else {
          next.have = a.have;
        }

        // Ensure requiredKinds are available for rendering rows
        if (!next.requiredKinds || !next.requiredKinds.length) {
          next.requiredKinds = computeRequiredKindsFromTraining(training);
        }

        return next;
      });
    } catch {
      // ignore
    }
  }


  const step = useMemo(
    () => training?.steps?.[stepIndex] || null,
    [training, stepIndex]
  );

  // Detect quizzes and normalize
  useEffect(() => {
    setQuizStep(null);
    setAnswers([]);
    setResult(null);
    if (!step) return;

    const urlGuess =
      step?.contentUrlSas || step?.contentUrl || step?.srcSas || step?.src || "";
    const looksLikeQuizFile =
      typeof urlGuess === "string" && urlGuess.toLowerCase().endsWith(".json");
    const isQuizType = step?.type === "quiz";

    if (isQuizType && Array.isArray(step?.questions)) {
      setQuizStep({
        title: step.title || t("training_player.quiz_title_fallback"),
        questions: step.questions,
      });
      return;
    }

    if (isQuizType || looksLikeQuizFile) {
      const url = step?.contentUrlSas || step?.srcSas || step?.contentUrl || step?.src;
      if (!url) return;

      let alive = true;
      setQuizLoading(true);
      fetch(url)
        .then(async (r) => {
          if (!r.ok) throw new Error("Failed to load quiz file");
          return r.json();
        })
        .then((data) => {
          if (!alive) return;
          const qs = (data?.questions || []).map((q) => ({
            id: q.id,
            text: q.text,
            choices: q.options || q.choices || [],
            answerIndex: typeof q.answerIndex === "number" ? q.answerIndex : undefined,
          }));
          setQuizStep({
            title: data?.title || step?.title || t("training_player.quiz_title_fallback"),
            questions: qs,
          });
        })
        .catch(() => {
          if (alive) setError(t("training_player.quiz_unavailable"));
        })
        .finally(() => {
          if (alive) setQuizLoading(false);
        });

      return () => { alive = false; };
    }
  }, [step, t]);

  // Progress math (x current step means x-1 steps completed)
  const stepsCount = training?.steps?.length || 0;
  const completedSteps = Math.max(0, Math.min(stepIndex, Math.max(0, stepsCount - 1)));
  const progressPct = stepsCount > 0 ? (completedSteps / stepsCount) * 100 : 0;

  function computeRequiredKindsFromTraining(tr) {
    return [
      ...(tr?.ssmRequired ? ["SSM"] : []),
      ...(tr?.suRequired ? ["SU"] : []),
    ];
  }

  function next() {
    if (!training) return;
    if (stepIndex < stepsCount - 1) {
      setStepIndex((i) => i + 1);
      setResult(null);
    }
  }
  function prev() {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setResult(null);
    }
  }

  async function onSubmitQuiz() {
    if (!training) return;
    if (!quizStep || !Array.isArray(quizStep.questions) || quizStep.questions.length === 0) {
      setError(t("training_player.quiz_not_loaded"));
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await submitQuiz(user?.id, training.id, answers, stepIndex);
      setResult(res); // { correct, total, passed }
      // if passed, refresh allowed (backend may set PASSED_QUIZ/awaiting)
      if (res?.passed) refreshAllowed();
    } catch (e) {
      setError(e?.message || t("training_player.quiz_submit_failed"));
    } finally {
      setSubmitting(false);
    }
  }

  // Single-button: request backend to generate/sign and deliver via email
  async function onSignCertificate() {
    setWorkingKind("SIGN");
    setSignFlows(null);
    try {
      // Check profile completion first
      const status = await getUserCompletionStatus();
      if (!status?.completed) {
        const missing = Array.isArray(status?.missing) ? status.missing.join(", ") : "";
        alert(
          t("training_player.profile_incomplete", "Your profile is incomplete: {{fields}}. Please update your profile before requesting the certificate.", { fields: missing })
        );
        nav("/profile");
        return;
      }

      const res = await signCertificate(id);
      // Backend now returns { ok, message, flows: [{kind, id, flowId, lastStep, status}] }
      if (res?.flows?.length) {
        setSignFlows(res.flows);
      } else {
        // Fallback: no flows info — still inform user
        alert(t("training_player.sign_certificate_sent", "You'll receive your document on email, it might take a while until received"));
      }
      await refreshAllowed();
    } catch (e) {
      const msg = e?.message || "";
      if (msg === "signing_config_missing" || msg.includes("signing_config_missing")) {
        alert(t("training_player.signing_config_missing", "The signing flow has not been configured yet. Please contact a platform admin."));
      } else {
        alert(msg || t("training_player.sign_certificate_failed", "Failed to request certificate signing"));
      }
    } finally {
      setWorkingKind(null);
    }
  }

  if (loading)
    return (
      <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-slate-500">
        {t("training_player.loading")}
      </div>
    );
  if (error)
    return (
      <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-red-600">
        {error}
      </div>
    );
  if (!training) return null;

  const showCertSection =
    certAllowed?.allowed ||
    // show once user is at / beyond last step (even without quiz)
    (stepsCount === 0 || stepIndex >= stepsCount - 1) ||
    // or after passing a quiz
    !!result?.passed;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-3 text-sm text-slate-500">
        <button className="hover:underline" onClick={() => nav("/trainings")}>
          {t("training_player.back_to_trainings")}
        </button>
      </div>

      <Card
        title={training.title}
        actions={
          <div className="text-sm text-slate-500 flex items-center gap-3">
            <div>
              {t("training_player.step_of", {
                current: stepIndex + 1,
                total: stepsCount,
              })}
            </div>
            <div className="w-32 h-1.5 bg-slate-200 rounded-full">
              <div
                className="h-1.5 bg-blue-600 rounded-full transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        }
      >
        {/* Video */}
        {step?.type === "video" && (
          <div className="space-y-3">
            <div className="aspect-video w-full bg-slate-100 rounded-xl overflow-hidden">
              <video
                src={step.srcSas || step.src}
                controls
                className="w-full h-full"
              />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={prev} disabled={stepIndex === 0}>
                {t("training_player.nav_back")}
              </Button>
              <Button onClick={next}>{t("training_player.nav_next")}</Button>
            </div>
          </div>
        )}

        {/* Document (PDF) */}
        {step?.type === "doc" &&
          !(step?.contentUrlSas || "").toLowerCase().endsWith(".json") && (
            <div className="space-y-3">
              {(() => {
                const pdfUrl = step.contentUrlSas || step.contentUrl;
                return (
                  <>
                    <div className="w-full h-[70vh] bg-slate-100 rounded-xl overflow-hidden">
                      <embed
                        src={pdfUrl}
                        type="application/pdf"
                        className="w-full h-full"
                      />
                    </div>

                    <div className="text-sm text-slate-500">
                      <Trans i18nKey="training_player.doc_fallback">
                        If the preview doesn’t load,{" "}
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          open the document in a new tab
                        </a>
                        .
                      </Trans>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        variant="ghost"
                        onClick={prev}
                        disabled={stepIndex === 0}
                      >
                        {t("training_player.nav_back")}
                      </Button>
                      <Button onClick={next}>
                        {t("training_player.doc_ack")}
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

        {/* Quiz */}
        {(step?.type === "quiz" ||
          (step?.contentUrlSas || step?.contentUrl || "")
            .toLowerCase()
            .endsWith(".json")) && (
          <div className="space-y-4">
            <div className="text-lg font-semibold">
              {quizStep?.title ||
                step?.title ||
                t("training_player.quiz_title_fallback")}
            </div>

            {quizLoading ? (
              <div className="text-sm text-slate-500">
                {t("training_player.quiz_loading")}
              </div>
            ) : quizStep && Array.isArray(quizStep.questions) ? (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmitQuiz();
                }}
              >
                {quizStep.questions.map((q, qi) => {
                  const choices = q.choices || q.options || [];
                  return (
                    <div
                      key={q.id || qi}
                      className="p-3 rounded-xl border border-slate-200"
                    >
                      <div className="font-medium mb-2">
                        {qi + 1}. {q.text}
                      </div>
                      <div className="space-y-2">
                        {choices.map((c, ci) => {
                          if (!c || String(c).trim() === "") return null;
                          return (
                            <label
                              key={ci}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                name={`q_${qi}_${ci}`}
                                checked={Array.isArray(answers[qi]) ? answers[qi].includes(ci) : answers[qi] === ci}
                                disabled={submitting}
                                onChange={() => {
                                  setAnswers((prev) => {
                                    const a = [...prev];
                                    const cur = Array.isArray(a[qi]) ? [...a[qi]] : (a[qi] !== undefined ? [a[qi]] : []);
                                    const idx = cur.indexOf(ci);
                                    if (idx >= 0) cur.splice(idx, 1);
                                    else cur.push(ci);
                                    a[qi] = cur;
                                    return a;
                                  });
                                  setResult(null);
                                }}
                              />
                              <span>{c}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between items-center">
                  <Button
                    variant="ghost"
                    onClick={prev}
                    type="button"
                    disabled={stepIndex === 0 || submitting}
                  >
                    {t("training_player.nav_back")}
                  </Button>
                  {result?.passed && stepIndex < stepsCount - 1 ? (
                    <Button type="button" onClick={next} disabled={submitting}>
                      {t("training_player.nav_next")}
                    </Button>
                  ) : (
                    <Button type="submit" disabled={submitting}>
                      {submitting
                        ? t("training_player.quiz_checking")
                        : t("training_player.quiz_submit")}
                    </Button>
                  )}
                </div>
              </form>
            ) : (
              <div className="text-sm text-red-600">
                {t("training_player.quiz_unavailable")}
              </div>
            )}

            {result && (
              <div
                className={`p-3 rounded-xl ${
                  result.passed
                    ? "bg-green-50 border border-green-200"
                    : "bg-yellow-50 border border-yellow-200"
                }`}
              >
                <div className="font-medium">
                  {result.passed
                    ? t("training_player.result_passed")
                    : t("training_player.result_not_yet")}
                </div>
                <div className="text-sm text-slate-600">
                  {t("training_player.result_score", {
                    correct: result.correct,
                    total: result.total,
                  })}
                </div>
                {!result.passed && (
                  <div className="mt-2 text-sm">
                    {t("training_player.result_try_again")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Certificates (per-kind) */}
        {showCertSection && (
          <div className="mt-6 space-y-3">
            <div className="font-semibold">{t("training_player.certificate_section")}</div>
            <div className="text-sm text-slate-600">
              {t("training_player.certificate_instructions")}
            </div>

            <div className="space-y-2">
              <div>
                <Button onClick={onSignCertificate} disabled={workingKind === "SIGN"}>
                  {workingKind === "SIGN" ? t("training_player.working") : t("training_player.sign_certificate")}
                </Button>
              </div>
              {signFlows && signFlows.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {signFlows.map((f) => (
                    <span
                      key={f.kind}
                      className="px-2 py-0.5 rounded-full text-xs border bg-green-50 border-green-200 text-green-800"
                    >
                      {f.kind}: {f.status || f.lastStep}
                    </span>
                  ))}
                  <div className="w-full text-sm text-slate-500">
                    {t("training_player.sign_certificate_sent", "You'll receive your document on email, it might take a while until received")}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  {t("training_player.sign_certificate_explain", "You'll receive your document on email, it might take a while until received")}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => nav("/dashboard")}>
                {t("training_player.go_dashboard")}
              </Button>
              <Button variant="ghost" onClick={() => nav("/trainings")}>{t("training_player.back_my_trainings")}</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
